import { useState } from "react";
import {
  ArrowLeft,
  FileSignature,
  LockKeyhole,
  Mail,
  Pencil,
  ScrollText,
  UsersRound,
  Users,
} from "lucide-react";
import { KampagnenCard } from "@/components/admin/mail/kampagnen-card";
import { VertragCard } from "@/components/admin/vertrag-card";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TENANT_STATUS_LABELS } from "@nl/shared";
import { TenantDialog } from "@/components/admin/tenant-dialog";
import { BausteineCard } from "@/components/bausteine-card";
import { TenantGroupsCard } from "@/components/admin/tenant-groups-card";
import { TenantUsersCard } from "@/components/admin/tenant-users-card";
import { EinheitFreigabenCard } from "@/components/einheit-freigaben-card";
import { EmailDomainCard } from "@/components/email-domain-card";
import { useAuditLog, useTenant } from "@/lib/queries";

import { Link, createFileRoute } from "@tanstack/react-router";
function SparkasseDetail() {
  const { id } = Route.useParams();

  const { data: sparkasse, isLoading } = useTenant(id);
  // Queried for this tenant explicitly rather than through the
  // impersonation header, so the page works without switching into it.
  const { data: audit = [] } = useAuditLog(id);
  const [editingTenant, setEditingTenant] = useState(false);

  if (isLoading) {
    return (
      <Card className="p-8 text-center">
        <p className="text-muted-foreground">Wird geladen ...</p>
      </Card>
    );
  }

  if (!sparkasse) {
    return (
      <Card className="p-8 text-center">
        <p className="text-muted-foreground">Sparkasse nicht gefunden.</p>
        <Button asChild className="mt-3">
          <Link to="/admin/mandanten">Zurück</Link>
        </Button>
      </Card>
    );
  }

  return (
    <div className="space-y-7">
      <header className="reveal space-y-4 border-b border-[var(--rule)] pb-6">
        <Link to="/admin/mandanten"
          className="inline-flex items-center gap-1.5 font-bold text-[11.5px] tracking-[0.06em] text-muted-foreground uppercase hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" />
          Mandanten
        </Link>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-[36px] leading-[1.05] font-medium tracking-tight">
              {sparkasse.name}
            </h1>
            <p className="mt-2 text-[14px] text-muted-foreground">
              {sparkasse.internal ? "Interner Mandant der Plattformkonten · " : ""}
              {TENANT_STATUS_LABELS[sparkasse.status]} · Region {sparkasse.region} · seit {new Date(sparkasse.createdAt).toLocaleDateString("de-DE")}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => setEditingTenant(true)}
              className="gap-2"
            >
              <Pencil className="h-4 w-4" />
              Bearbeiten
            </Button>
          </div>
        </div>
      </header>

      <Tabs defaultValue="freischaltung" className="gap-6">
        <TabsList variant="line">
          <TabsTrigger value="freischaltung">
            <LockKeyhole />
            Freischaltung
          </TabsTrigger>
          <TabsTrigger value="users">
            <Users />
            Benutzer
          </TabsTrigger>
          {!sparkasse.internal && (
            <TabsTrigger value="gruppen">
              <UsersRound />
              Gruppen
            </TabsTrigger>
          )}
          {!sparkasse.internal && (
            <>
              <TabsTrigger value="mails">
                <Mail />
                Mails
              </TabsTrigger>
              <TabsTrigger value="vertrag">
                <FileSignature />
                Vertrag
              </TabsTrigger>
            </>
          )}
          <TabsTrigger value="audit">
            <ScrollText />
            Protokoll
          </TabsTrigger>
        </TabsList>

        <TabsContent value="freischaltung" className="space-y-6">
          <BausteineCard
            tenantId={sparkasse.id}
            tenantName={sparkasse.name}
            bausteine={sparkasse.bausteine}
          />
          <EinheitFreigabenCard tenantId={sparkasse.id} eyebrow={false} />
        </TabsContent>

        <TabsContent value="users" className="space-y-6">
          <EmailDomainCard
            tenantId={sparkasse.id}
            tenantName={sparkasse.name}
            domains={sparkasse.allowedEmailDomains}
          />
          <TenantUsersCard
            tenantId={sparkasse.id}
            internal={sparkasse.internal}
            description="Im Regelfall pflegt die Führungskraft der Sparkasse die Konten selbst. Hier lassen sich Zugänge anlegen, Rollen vergeben und Konten sperren."
          />
        </TabsContent>

        {!sparkasse.internal && (
          <TabsContent value="gruppen">
            <TenantGroupsCard tenantId={sparkasse.id} />
          </TabsContent>
        )}

        {!sparkasse.internal && (
          <>
            <TabsContent value="mails">
              <KampagnenCard tenantId={sparkasse.id} />
            </TabsContent>
            <TabsContent value="vertrag">
              <VertragCard key={sparkasse.id} tenant={sparkasse} />
            </TabsContent>
          </>
        )}

        <TabsContent value="audit">
          <Card className="gap-0 overflow-hidden p-0">
            <div className="border-b border-[var(--rule)] px-5 py-4">
              <h2 className="font-display text-[20px] font-medium tracking-tight">
                Letzte Änderungen an dieser Sparkasse
              </h2>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Zeitpunkt</TableHead>
                  <TableHead>Akteur</TableHead>
                  <TableHead>Aktion</TableHead>
                  <TableHead>Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {audit.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell className="font-mono text-[12.5px] text-muted-foreground">
                      {new Date(entry.at).toLocaleString("de-DE", {
                        dateStyle: "short",
                        timeStyle: "short",
                      })}
                    </TableCell>
                    <TableCell className="text-[13.5px]">
                      {entry.actor}
                    </TableCell>
                    <TableCell className="text-[13.5px] font-medium">
                      {entry.action}
                    </TableCell>
                    <TableCell className="text-[13.5px] text-muted-foreground">
                      {entry.detail ?? "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      {editingTenant && (
        <TenantDialog tenant={sparkasse} onClose={() => setEditingTenant(false)} />
      )}
    </div>
  );
}

export const Route = createFileRoute("/_app/admin/mandanten/$id")({
  component: SparkasseDetail,
});
